// import React from 'react';
import { motion } from 'framer-motion';
import Navbar from './navbar';
import Footer from './Footer';  // Add this import
import { FiTarget, FiCompass, FiLayers, FiTool, FiUsers, FiGlobe } from 'react-icons/fi';

const Mission = () => {
  const missionPoints = [
    {
      icon: <FiCompass className="w-5 h-5" />,
      title: "Sector-Specific Discovery",
      description: "Empower users to explore and understand AI use cases across diverse industry sectors and sub-sectors through intelligent navigation and categorization."
    },
    {
      icon: <FiLayers className="w-5 h-5" />,
      title: "Comprehensive Coverage",
      description: "Maintain an extensive, up-to-date database of AI implementations, ensuring users have access to the latest and most relevant use cases in their field."
    },
    {
      icon: <FiTool className="w-5 h-5" />,
      title: "Practical Implementation",
      description: "Provide detailed insights, implementation guides, and best practices to help organizations successfully adopt AI solutions in their specific contexts."
    }
  ];

  const objectives = [
    {
      icon: <FiTarget className="w-5 h-5" />,
      title: "Democratize AI Knowledge",
      description: "Make AI use case discovery accessible to organizations of all sizes across sectors."
    },
    {
      icon: <FiUsers className="w-5 h-5" />,
      title: "Foster Innovation",
      description: "Enable cross-pollination of ideas by showcasing successful AI implementations across industries."
    },
    {
      icon: <FiGlobe className="w-5 h-5" />,
      title: "Drive Transformation",
      description: "Accelerate industry-wide digital transformation through informed AI adoption."
    }
  ];

  return (
    <div className="flex flex-col w-screen min-h-screen overflow-x-hidden font-sans">
      {/* Hero Section with Gradient */}
      <div className="w-screen min-h-screen bg-gradient-to-b from-indigo-900 via-blue-900 to-gray-900 text-white overflow-x-hidden">
        <Navbar />
        <div className="pt-32 pb-24 px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold mb-4 md:mb-6 leading-tight">
                Our Mission
                <span className="block text-orange-400 mt-1 md:mt-2 text-xl md:text-4xl">
                  Revolutionizing AI Use Case Discovery
                </span>
              </h1>
              <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                We are committed to transforming how organizations discover, understand, and implement AI solutions 
                by providing a comprehensive platform for exploring sector-specific use cases through intelligent search and navigation.
              </p>
            </motion.div>

            {/* Mission Statement Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-20">
              {missionPoints.map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.5 }}
                  className="bg-gradient-to-br from-gray-800 to-gray-900 p-4 md:p-6 rounded-xl shadow-lg border border-gray-700 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-orange-400 mb-3 md:mb-4">{point.icon}</div>
                  <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">{point.title}</h3>
                  <p className="text-sm md:text-base text-gray-300 leading-relaxed">{point.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Objectives Section */}
      <div className="bg-white py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Strategic Objectives
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our mission is supported by clear objectives that guide our platform's development 
              and ensure we deliver maximum value to our users.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {objectives.map((objective, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.2, duration: 0.5 }}
                className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-orange-500 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 mb-4">
                  {objective.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{objective.title}</h3>
                <p className="text-gray-600">{objective.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Impact Section */}
      <div className="bg-gray-50 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Our Impact
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Through our platform, we're helping organizations across industries harness 
              the power of AI to drive innovation and achieve their strategic goals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-orange-500 mb-2">500+</div>
              <div className="text-gray-600">Use Cases Documented</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-orange-500 mb-2">50+</div>
              <div className="text-gray-600">Industry Sectors</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-orange-500 mb-2">1000+</div>
              <div className="text-gray-600">Organizations Supported</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-orange-500 mb-2">95%</div>
              <div className="text-gray-600">User Satisfaction</div>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer /> 
    </div>
  );
};

// Add content-only export
export const MissionContent = () => {
  const missionPoints = [
    {
      icon: <FiCompass className="w-6 h-6" />,
      title: "Sector-Specific Discovery",
      description: "Empower users to explore and understand use cases across diverse industry sectors and sub-sectors through intelligent navigation."
    },
    {
      icon: <FiLayers className="w-6 h-6" />,
      title: "Comprehensive Coverage",
      description: "Maintain an extensive, up-to-date database of implementations, ensuring users have access to the latest cases in their field."
    },
    {
      icon: <FiTool className="w-6 h-6" />,
      title: "Practical Implementation",
      description: "Provide detailed insights and best practices to help organizations successfully adopt solutions."
    }
  ];

  const objectives = [
    {
      icon: <FiTarget className="w-6 h-6" />,
      title: "Democratize AI Knowledge",
      description: "Make AI use case discovery accessible to organizations of all sizes across sectors."
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: "Foster Innovation",
      description: "Enable cross-pollination of ideas by showcasing successful AI implementations across industries."
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: "Drive Transformation",
      description: "Accelerate industry-wide digital transformation through informed AI adoption."
    }
  ];

  return (
    <div className="space-y-10">
      {/* Header Section */}
      <header>
        <h2
          style={{
            fontFamily: 'Sora, sans-serif',
            fontWeight: 600,
            fontSize: '32px',
            lineHeight: '150%',
            color: 'rgba(255, 84, 31, 1)',
          }}
          className="mb-4"
        >
          Our Mission
        </h2>
        <div className="mb-6">
          <span
            style={{
              fontFamily: 'Sora, sans-serif',
              fontWeight: 700,
              fontSize: '24px',
              lineHeight: '200%',
              color: 'rgba(255,255,255,1)',
            }}
            className="block mb-2"
          >
            Revolutionizing AI Use Case Discovery
          </span>
          <span
            style={{
              fontFamily: 'Sora, sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '150%',
              color: 'rgba(140, 140, 140, 1)',
            }}
            className="block"
          >
            We are committed to transforming how organizations discover, understand, and implement AI solutions by providing a comprehensive platform for exploring sector-specific use cases through intelligent search and navigation.
          </span>
        </div>
      </header>

      {/* Mission Points Section */}
      <section className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 md:p-8">
        <h3
          style={{
            fontFamily: 'Sora, sans-serif',
            fontWeight: 600,
            fontSize: '24px',
            lineHeight: '150%',
            color: 'rgba(255, 255, 255, 1)',
            letterSpacing: '0.01em',
          }}
          className="mb-6"
        >
          Mission Pillars
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {missionPoints.map((item, i) => (
            <div
              key={i}
              className="flex flex-col justify-start px-4 py-6 bg-black/50 rounded-lg border border-white/10"
            >
              <div className="text-orange-400 mb-3">{item.icon}</div>
              <h4
                style={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  lineHeight: '150%',
                  color: 'rgba(255,255,255,1)',
                  marginBottom: '10px',
                  letterSpacing: '0.01em',
                }}
                className="mb-2"
              >
                {item.title}
              </h4>
              <p
                style={{
                  fontFamily: 'Lora, serif',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '160%',
                  color: 'rgba(200,200,200,1)',
                  letterSpacing: '0.01em',
                }}
              >
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Strategic Objectives Section */}
      <section className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 md:p-8">
        <h3
          style={{
            fontFamily: 'Sora, sans-serif',
            fontWeight: 600,
            fontSize: '24px',
            lineHeight: '150%',
            color: 'rgba(255, 255, 255, 1)',
            letterSpacing: '0.01em',
          }}
          className="mb-6"
        >
          Strategic Objectives
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {objectives.map((obj, i) => (
            <div
              key={i}
              className="flex flex-col justify-start px-4 py-6 bg-black/50 rounded-lg border border-white/10"
            >
              <div className="text-orange-400 mb-3">{obj.icon}</div>
              <h4
                style={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  lineHeight: '150%',
                  color: 'rgba(255,255,255,1)',
                  marginBottom: '10px',
                  letterSpacing: '0.01em',
                }}
                className="mb-2"
              >
                {obj.title}
              </h4>
              <p
                style={{
                  fontFamily: 'Lora, serif',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '160%',
                  color: 'rgba(200,200,200,1)',
                  letterSpacing: '0.01em',
                }}
              >
                {obj.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Impact Statistics Section */}
      <section className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 md:p-8 relative">
        <h3
          style={{
            fontFamily: 'Sora, sans-serif',
            fontWeight: 600,
            fontSize: '24px',
            lineHeight: '150%',
            color: 'rgba(255, 255, 255, 1)',
          }}
          className="mb-3"
        >
          Our Impact
        </h3>
        <p
          style={{
            fontFamily: 'Lora, serif',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '160%',
            color: 'rgba(140,140,140,1)',
          }}
          className="max-w-3xl mb-6"
        >
          Through our platform, we&apos;re helping organizations across industries harness the power of AI to drive
          innovation and achieve their strategic goals.
        </p>

        {/* Metrics Container */}
        <div className="mt-6 rounded-xl border border-white/10 bg-black/30 px-4 py-5 md:px-8 md:py-6 grid grid-cols-2 md:grid-cols-4">
          {[
            ["500+", "Use Cases Documented"],
            ["50+", "Industry Sectors"],
            ["1000+", "Organizations Supported"],
            ["95%", "User Satisfaction"],
          ].map(([value, label], index) => (
            <div
              key={index}
              className={`flex flex-col justify-start mb-4 md:mb-0 pr-4 md:pr-6 ${
                index % 2 === 0 ? 'mr-2' : ''
              } ${index > 0 ? 'md:border-l md:border-white/10 md:pl-8' : 'md:pl-0'} ${
                index % 2 === 1 ? 'pl-4' : ''
              }`}
            >
              <div
                style={{
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                  fontSize: '32px',
                  lineHeight: '140%',
                  color: 'rgba(255,84,31,1)',
                }}
                className="mb-2"
              >
                {value}
              </div>
              <div
                style={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: 500,
                  fontSize: '15px',
                  lineHeight: '150%',
                  color: 'rgba(204,204,204,1)',
                }}
                className="whitespace-pre-line"
              >
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Decorative diagonal light / subtle spark accents */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
          <div className="absolute -top-10 right-[-20%] w-[160%] h-full rotate-[20deg] bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-10" />
          <div className="absolute -bottom-4 left-10 w-1 h-1 rounded-full bg-orange-500/80" />
          <div className="absolute bottom-6 left-[45%] w-1 h-1 rounded-full bg-orange-500/70" />
          <div className="absolute bottom-2 right-[22%] w-1 h-1 rounded-full bg-orange-500/60" />
        </div>
      </section>
    </div>
  );
};

export default Mission;